import IconCommon from '@/components/Common/DesignSystem/Icon/IconCommon';
import { GlobalStoreCtx } from '@/stores/GlobalStore';
import { EN_SCROLL_Y_DIRECTION, ScrollStoreCtx } from '@/stores/ScrollStore';
import { debounce, throttle } from '@/utils';
import { scrollWithAnim } from '@/utils/functions/dom/scrollWithAnim';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import styles from './ToTopButton.module.scss';

enum EN_VISIBILITY {
  NONE = 'NONE',
  SHOW = 'SHOW',
  HIDE = 'HIDE',
}

interface IToTopButtonProps {
  /**
   * 등장 기준점.
   *
   * - `HTMLElement`면 해당 요소의 상단이 스크롤 위치를 지나면 등장한다.
   * - `number`면 스크롤 포지션이 해당 수치를 넘어가면 등장한다.
   * - `null`이면 노출하지 않는다.
   *
   * @default 150
   */
  toggleAnchor?: HTMLElement | number | null;
  /** 상위 컨테이너 element (없으면 window 사용) */
  container?: HTMLElement | null;
  /** 추가 적용 className */
  className?: string;
  /** 미노출 > 노출 시 적용되는 className */
  showClassName?: string;
  /** 노출 > 미노출 시 적용되는 className */
  hideClassName?: string;
  /** 클릭 이벤트 */
  onClick?: () => void;
  /** 노출, 미노출 이벤트 */
  onVisibleChange?: (boolean) => void;
}

/**
 * 우측 하단에 노출되는 맨위로 버튼
 *
 * @see {@link https://yanolja.atlassian.net/wiki/spaces/UXD/pages/41034774 가이드 문서}
 */
const ToTopButton: React.FC<IToTopButtonProps> = ({
  className,
  showClassName,
  hideClassName,
  toggleAnchor = 150,
  container,
  onClick,
  onVisibleChange,
}) => {
  const globalStore = useContext(GlobalStoreCtx);
  const scrollStore = useContext(ScrollStoreCtx);
  const visibility = useRef<EN_VISIBILITY>(EN_VISIBILITY.NONE);
  const [visibilityStyle, setVisibilityStyle] = useState<string>('');
  const scrollToTop = useCallback(() => {
    scrollWithAnim(0, { scrollContainer: scrollStore.body });
    scrollStore.yDirection = EN_SCROLL_Y_DIRECTION.DOWN;
    onClick?.();
  }, [onClick]);
  const updateVisibility = (isVisible: boolean): void => {
    const curVisibility = visibility.current;
    const newVisibility = isVisible ? EN_VISIBILITY.SHOW : EN_VISIBILITY.HIDE;
    if (curVisibility === newVisibility) {
      return;
    }
    // 상태 변경
    visibility.current = newVisibility;
    // 스타일 변경
    const classNames: Array<string | undefined> = [];
    if (curVisibility === EN_VISIBILITY.NONE) {
      // 첫 상태에서는 애니메이션 없이 적용하기 위해 duration 0 적용
      classNames.push(styles.zeroDuration);
    }
    if (newVisibility === EN_VISIBILITY.SHOW) {
      classNames.push(styles.show, showClassName);
    } else if (newVisibility === EN_VISIBILITY.HIDE) {
      classNames.push(styles.hide, hideClassName);
    }
    setVisibilityStyle(clsx([...classNames]));
    // 상태 변경 이벤트 전파
    onVisibleChange?.(isVisible);
  };

  // toggleAnchor가 포지션인 경우
  useEffect(() => {
    if (typeof toggleAnchor !== 'number') {
      return;
    }
    const parentsElement = container ?? window;
    const checkVisible = (): void => {
      const topBannerHeight = globalStore.ShowSmartBanner ? 87 : 0;
      updateVisibility(scrollStore.position.y - topBannerHeight >= toggleAnchor);
    };
    checkVisible();
    const recomputeOnScroll = throttle(checkVisible, 20);
    parentsElement.addEventListener('scroll', recomputeOnScroll);

    return () => parentsElement.removeEventListener('scroll', recomputeOnScroll);
  }, [globalStore.ShowSmartBanner, scrollStore.isTop, toggleAnchor]);

  // toggleAnchor가 element인 경우
  useEffect(() => {
    if (!toggleAnchor || typeof toggleAnchor !== 'object') {
      return;
    }
    const parentsElement = container ?? window;
    const getAnchorPos = (): void => {
      const clientTop = toggleAnchor?.getBoundingClientRect().top ?? 0;
      updateVisibility(scrollStore.position.y >= clientTop + window.pageYOffset);
    };
    getAnchorPos();

    // [todo] use IntersectionObserver
    const recomputeOnScroll = throttle(getAnchorPos, 500);
    const recomputeOnResize = debounce(getAnchorPos, 100, { trailing: true });
    parentsElement.addEventListener('scroll', recomputeOnScroll);
    parentsElement.addEventListener('resize', recomputeOnResize);

    return () => {
      parentsElement.removeEventListener('scroll', recomputeOnScroll);
      parentsElement.removeEventListener('resize', recomputeOnResize);
    };
  }, [globalStore.ShowSmartBanner, toggleAnchor]);

  return (
    <div className={clsx(styles.container, className, visibilityStyle)}>
      <button onClick={scrollToTop} className={styles.button}>
        <IconCommon type="icon_arrowless_fill" className={styles.icon} />
      </button>
    </div>
  );
};

export default observer(ToTopButton);
